import React, { useEffect, useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import {
  BlueBirdTheme,
  Button,
  Divider,
  Dropdown,
  Form,
  Radio,
  RadioGroup,
  Typography,
} from "@kitbird/core";
import styled from "styled-components";
import { useQuery } from "react-query";
import { FormContainer } from "../../../../../components/Form/Form.style";
import { ReactComponent as CrossIconLg } from "../../../../../assets/icons/cross_icon_lg.svg";
import { ReactComponent as PlusIcon } from "../../../../../assets/icons/plus.svg";
import pdfIcon from "../../../../../assets/icons/pdf_file.svg";
import pngIcon from "../../../../../assets/images/png_icon.png";
import jpgIcon from "../../../../../assets/images/jpg_icon.png";
import useVendor from "../../useVendor";
import { StatusFlagColumn } from "../../constants";
import { getPools, IPoolData } from "../../../../../services/poolService";
import { getMasterDepartment, IGetMasterDepartmentData } from "../../../../../services/masterDepartmentService";
import { byteStringToFileSize } from "../../../../../utils/common";
import { ViewIcon } from "../../../../../assets/icons";
import useAuth from "../../../../../components/Authentication/useAuth";
import { listCodeTypeAll } from "../../../../Transaction/CreateReceiptSubmission/constants";
import { IOptionItem } from "../../../../../typings/common";

interface VendorFormProps {
  onClose: () => void;
  useCustomHook: ReturnType<typeof useVendor>;
}

const vendorCostOpts = [
  { label: "SIMPLE", value: "SIMPLE" },
];

const {
  danger,
  gray,
  primary,
  secondary,
  white,
} = BlueBirdTheme.colors;

const HeadContainer = styled.div`
  margin: -16px -16px 16px;
  padding: 24px;
  border-bottom: 1px solid ${secondary[200]};
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin: -16px;
`;

const CustomFormContainer = styled(FormContainer) <{ $customBg?: boolean, $maxHeight?: string }>`
  padding: 24px;
  ${({ $maxHeight }) => ($maxHeight ? `max-height: ${$maxHeight}` : "")};
  overflow-y: auto;
  ${({ $customBg }) => ($customBg && `
    background-color: ${gray.background};

    label {
      z-index: 0;
    }
  `)};
`;

const AddWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
`;

const iconByExtension = (extension?: string) => {
  const icon = extension ? {
    pdf: pdfIcon,
    png: pngIcon,
    jpg: jpgIcon,
    jpeg: jpgIcon,
  }[extension] : pngIcon;
  return <img src={icon} width={24} height={24} />;
};

const VendorForm = ({ useCustomHook }: VendorFormProps) => {
  const { userAccount } = useAuth();
  const {
    initPoolVendor,
    bankOpts,
    methods: {
      control, getValues, setValue, formState, watch,
    },
    handleUpload,
    editValues,
    files,
    allDepartment,
    handleDisplayDoc,
  } = useCustomHook;
  const { errors } = formState;
  const {
    fields, append, remove,
  } = useFieldArray({
    control,
    name: "pool_department",
  });
  const [isNotApproved, setNotApproved] = useState(true);
  const [isAllApproved, setAllApproved] = useState(true);
  const [poolOpts, setPoolOpts] = useState<IOptionItem[]>([]);
  const [codeTypeOpts, setCodeTypeOpts] = useState<IOptionItem[]>([]);
  const [areaOpts, setAreaOpts] = useState<IOptionItem[]>([]);

  useEffect(() => {
    if (userAccount.position_info) {
      const codeTypeOptions: string[] = [];
      const areaStringOptions: string[] = [];
      const poolStringOptions: string[] = [];
      userAccount.position_info.forEach((item) => {
        const codeTypeOption = JSON.stringify({
          label: listCodeTypeAll
            .find((code) => code.code_type === item.code_type)?.code_type_desc
            || item.code_type,
          value: item.area_id,
        });
        if (!codeTypeOptions.includes(codeTypeOption)) {
          codeTypeOptions.push(codeTypeOption);
        }

        const stringArea = JSON.stringify({
          label: item.area_name.replace(/[^a-zA-Z\s]/g, ""),
          value: item.area_id,
        });
        if (!areaStringOptions.includes(stringArea)) {
          areaStringOptions.push(stringArea);
        }

        const stringPool = JSON.stringify({
          label: `${item.pool_name} • ${item.pool_id}`,
          value: item.pool_id,
        });
        if (!poolStringOptions.includes(stringPool)) {
          poolStringOptions.push(stringPool);
        }
      });
      setCodeTypeOpts(codeTypeOptions.map((item) => JSON.parse(item)));
      setAreaOpts(areaStringOptions.map((item) => JSON.parse(item)));
      setPoolOpts(poolStringOptions.map((item) => JSON.parse(item)));
    }
  }, [userAccount.position_info]);

  useEffect(() => {
    if (editValues) {
      setNotApproved(!(
        editValues?.flag_column === StatusFlagColumn.SOME_APPROVED
        || editValues?.flag_column === StatusFlagColumn.ALL_APPROVED
      ));
      setAllApproved(!(
        editValues?.flag_column === StatusFlagColumn.SOME_APPROVED
        || editValues?.flag_column === StatusFlagColumn.NOT_APPROVED
      ));
    }
  }, [editValues]);

  const renderUploadForm = <div>
    <Form
      label="Lampiran"
      type="file"
      accept=".pdf, .jpeg, .jpg, .png"
      onChange={(e) => {
        if (e.target.files) handleUpload(e.target.files[0]);
      }}
      value={watch("vendor_files.0")?.file_name || ""}
      disabled={!!editValues}
      error={
        (errors && errors.vendor_files)
          ? { message: errors.vendor_files.message || "Harap mengisi Lampiran" }
          : undefined
      }
    />
    <Typography level={6} color={gray.disable}>{!errors.vendor_files && "PDF, PNG, JPG (maks. 10 MB)"}</Typography>
  </div>;

  return (
    <form>
      {editValues?.vendor_code && (
        <HeadContainer>
          <Typography level={6} color={gray.subtitle}>Kode vendor</Typography>
          <br />
          <Typography level={4} color={gray.title} fontWeight="medium">{editValues.vendor_code}</Typography>
        </HeadContainer>
      )}
      <GridContainer>
        <CustomFormContainer>
          <Typography level={5} fontWeight="bold" color={gray.subtitle}>Detail vendor</Typography>
          <div className="grid-form">
            <Controller
              control={control}
              name="type_vendor"
              render={({
                field: {
                  name, onChange, ref, value,
                },
                fieldState: { error },
              }) => (
                <Dropdown
                  name={name}
                  id="type_vendor"
                  label="Tipe vendor"
                  options={vendorCostOpts}
                  ref={ref}
                  value={value}
                  onSelected={(item) => onChange(item.value)}
                  error={error && { message: error.message || "" }}
                  disabled={!!editValues}
                />
              )}
            />
            <Controller
              control={control}
              name="vendor_name"
              render={({ field, fieldState: { error } }) => (
                <Form
                  {...field}
                  label="Nama vendor"
                  error={error && { message: error.message || "" }}
                  disabled={!!editValues}
                />
              )}
            />
          </div>
          <Controller
            control={control}
            name="vendor_address"
            render={({ field, fieldState: { error } }) => (
              <Form
                {...field}
                label="Alamat vendor"
                error={error && { message: error.message || "" }}
                disabled={!!editValues}
              />
            )}
          />
          {editValues
            ? <div>
              <RadioGroup
                onChange={(e) => setValue("status_vendor", e.target.value)}
                title="Status vendor"
                value={getValues("status_vendor")}
                className="radio-group"
                disabled={!isAllApproved}
              >
                <div
                  className="radio-group"
                >
                  <Radio
                    id="status_vendor_Y"
                    label="Aktif"
                    value="Y"
                    name="status_vendor"
                    disabled={!isAllApproved}
                  />
                  <Radio
                    id="status_vendor_N"
                    label="Non-aktif"
                    value="N"
                    name="status_vendor"
                    disabled={!isAllApproved}
                  />
                  <Radio
                    id="status_vendor_BL"
                    label="Blacklist"
                    value="BL"
                    name="status_vendor"
                    disabled={!isAllApproved}
                  />
                </div>
              </RadioGroup>
              <Typography level={7} color={danger[500]}>{errors.status_vendor?.message}</Typography>
            </div>
            : <></>
          }
          <Typography level={5} fontWeight="bold" color={gray.subtitle}>Detail rekening vendor</Typography>
          <div className="grid-form">
            <Controller
              control={control}
              name="bank_account"
              render={({
                field: {
                  name, onChange, ref, value,
                },
                fieldState: { error },
              }) => (
                <Dropdown
                  name={name}
                  id="bank_account"
                  label="Bank vendor"
                  options={bankOpts}
                  ref={ref}
                  value={value?.label}
                  onSelected={(item) => {
                    onChange(item);
                  }}
                  allowSearch
                  error={error && { message: "Harap mengisi Bank vendor" }}
                  disabled={!!editValues}
                />
              )}
            />
            <Controller
              control={control}
              name="bank_account_number"
              render={({ field, fieldState: { error } }) => (
                <Form
                  {...field}
                  label="No. rekening vendor"
                  type="number"
                  autoComplete="off"
                  error={error && { message: error.message || "" }}
                  disabled={!!editValues}
                />
              )}
            />
          </div>
          <Controller
            control={control}
            name="bank_account_name"
            render={({ field, fieldState: { error } }) => (
              <Form
                {...field}
                label="Nama pemilik rekening"
                error={error && { message: error.message || "" }}
                disabled={!!editValues}
              />
            )}
          />
          {editValues
            && files.map((doc) => (
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  padding: "12px 16px",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderRadius: "4px",
                  border: `1px solid ${gray.line}`,
                  color: gray.subtitle,
                  cursor: "pointer",
                }}
                onClick={handleDisplayDoc}
              >
                <div style={{
                  display: "flex",
                  gap: 16,
                  alignItems: "center",
                }}>
                  {iconByExtension(doc.file_name.split(".").pop())}
                  <div>
                    <Typography level={6}>
                      {doc.file_name}
                    </Typography>
                    <br />
                    <Typography level={6}>
                      {byteStringToFileSize(
                        doc.file_size,
                      )}
                    </Typography>
                  </div>
                </div>
                <Button
                  backgroundColor={primary[100]}
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  <ViewIcon color={gray.subtitle} />
                </Button>
              </div>
            ))
          }
          {(!editValues && !!watch("vendor_files").length) && renderUploadForm}
          {(!editValues && !watch("vendor_files").length) && renderUploadForm}
        </CustomFormContainer>
        <CustomFormContainer $customBg $maxHeight="532px">
          <Typography level={5} fontWeight="bold" color={gray.subtitle}>Area terkait</Typography>
          {fields.map((fieldItem, i) => (<>
            {i !== 0 && <Divider color={gray.line} />}
            <div key={fieldItem.id}>
              <div className={"grid-form"} >
                {fields.length > 1 && (<>
                  <div />
                  <div style={{ width: "100%" }} >
                    <Button
                      onClick={() => remove(i)}
                      textColor={gray.subtitle}
                      variant="text"
                      style={{ width: "fit-content", float: "inline-end" }}
                      disabled={!isAllApproved}
                    >
                      <CrossIconLg />
                    </Button>
                  </div>
                </>
                )}
                <Controller
                  control={control}
                  name={`pool_department.${i}.codeType`}
                  render={({
                    field: {
                      name, onChange, ref, value,
                    },
                    fieldState: { error },
                  }) => (
                    <Dropdown
                      name={name}
                      id={name}
                      label="Code type"
                      options={codeTypeOpts}
                      ref={ref}
                      value={value.label}
                      onSelected={onChange}
                      error={error && { message: error.message || "Harap mengisi Code type" }}
                      containerInputProps={!isAllApproved
                        ? undefined
                        : { style: { backgroundColor: white } }
                      }
                      disabled={!isAllApproved}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`pool_department.${i}.area`}
                  render={({
                    field: {
                      name, onChange, ref, value,
                    },
                    fieldState: { error },
                  }) => (
                    <Dropdown
                      name={name}
                      id={name}
                      label="Area"
                      options={areaOpts}
                      ref={ref}
                      value={value.label}
                      onSelected={onChange}
                      error={error && { message: error.message || "Harap mengisi Area" }}
                      containerInputProps={!isAllApproved
                        ? undefined
                        : { style: { backgroundColor: white } }
                      }
                      disabled={!isAllApproved}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`pool_department.${i}.pool`}
                  render={({
                    field: {
                      name, onChange, ref, value,
                    },
                    fieldState: { error },
                  }) => (
                    <Dropdown
                      name={name}
                      id={name}
                      label="Pool"
                      options={poolOpts}
                      ref={ref}
                      value={value.label}
                      onSelected={onChange}
                      error={error && { message: error.message || "Harap mengisi Pool" }}
                      containerInputProps={!isAllApproved
                        ? undefined
                        : { style: { backgroundColor: white } }
                      }
                      disabled={!isAllApproved}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`pool_department.${i}.department`}
                  render={({
                    field: {
                      name, onChange, ref, value,
                    },
                    fieldState: { error },
                  }) => (
                    <Dropdown
                      name={name}
                      id={name}
                      label="Departemen"
                      options={allDepartment.map((item) => (
                        { label: item.department, value: item.department }
                      ))}
                      ref={ref}
                      value={value}
                      onSelected={(item) => onChange(item.value)}
                      error={error && { message: error.message || "" }}
                      containerInputProps={!isAllApproved
                        ? undefined
                        : { style: { backgroundColor: white } }
                      }
                      disabled={!isAllApproved}
                    />
                  )}
                />
              </div>
            </div>
          </>))}
          <AddWrapper>
            <Button
              onClick={(e) => {
                e.preventDefault();
                append(initPoolVendor);
              }}
              variant="text"
              leftIcon={<PlusIcon />}
              disabled={!isAllApproved}
            >
              Tambah data
            </Button>
          </AddWrapper>
        </CustomFormContainer>
      </GridContainer>
    </form >
  );
};

export default VendorForm;
